import { useState } from "react";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IApiRequest,
  IAvailabilityLog,
  IUserResponse,
  ResourceActivityStatus,
  ResourceAvailabilityStatus,
} from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { IUserFormData } from "../../views/Config/Users/ConfigUsers.types";

const getUserUUID = createUUID();
const getUserAvailabilityLogsUUID = createUUID();
const createUserUUID = createUUID();
const updateUserUUID = createUUID();
const deactivateUserUUID = createUUID();
const activateUserUUID = createUUID();

export const useUser = () => {
  const stateManager = useComponentState();

  const [userDetails, setUserDetails] = useState<
    IUserResponse | Record<string, never>
  >();
  const [availabilityLogs, setAvailabilityLogs] = useState<IAvailabilityLog[]>(
    []
  );

  const getUser = (id: string) => {
    stateManager.abort(getUserUUID);
    return stateManager.run(async () => {
      const response = await subscriptionService.users.get(id);
      if (response.status === HTTP_STATUSES.OK) {
        setUserDetails(response.data);
      }
      return response;
    }, getUserUUID);
  };

  const getUserAvailabilityLogs = ({
    top,
    skip,
    sort,
    filters = {},
    params,
    signal,
  }: IApiRequest) => {
    stateManager.abort(getUserAvailabilityLogsUUID);
    return stateManager.run(async () => {
      const response = await subscriptionService.users.getUserAvailabilityLogs({
        top,
        skip,
        sort,
        filters,
        params,
        signal,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAvailabilityLogs(response.data);
      }
      return response;
    }, getUserAvailabilityLogsUUID);
  };

  const createUser = (data: IUserFormData): Promise<any> => {
    stateManager.abort(createUserUUID);
    return stateManager.run(
      async () =>
        subscriptionService.users.createUser({
          data: {
            ...data,
            departmentIds: data.departments?.map((dept) => dept.id) || [],
            roleIds: data.roles?.map((role) => role.id) || [],
          },
          toastMessageConfig: {
            success: { text: `${data.displayName} created successfully` },
          },
        }),
      createUserUUID
    );
  };

  const updateUser = (
    data: IUserFormData,
    options?: { onSuccess?: () => void }
  ) => {
    stateManager.abort(updateUserUUID);
    return stateManager.run(async () => {
      const response = await subscriptionService.users.updateUser({
        id: data.id,
        data: {
          activityStatus: ResourceActivityStatus.Idle,
          availabilityStatus: ResourceAvailabilityStatus.Available,
          ...data,
          departmentIds: data.departments?.map((dept) => dept.id) || [],
          roleIds: data.roles?.map((role) => role.id) || [],
        },
        toastMessageConfig: {
          success: { text: `${data.displayName} updated successfully` },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        options?.onSuccess?.();
      }
      return response;
    }, updateUserUUID);
  };

  const deactivateUser = (id: string, data: { modifiedOn: string }) => {
    stateManager.abort(deactivateUserUUID);
    return stateManager.run(async () => {
      const { status } = await subscriptionService.users.deactivateUser({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: `User deactivated successfully!`,
          },
        },
      });
      if (status === HTTP_STATUSES.OK) {
        return status;
      }
      return null;
    }, deactivateUserUUID);
  };

  const activateUser = (id: string, data: { modifiedOn: string }) => {
    stateManager.abort(activateUserUUID);
    return stateManager.run(async () => {
      const { status } = await subscriptionService.users.activateUser({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: `User activated successfully!`,
          },
        },
      });
      if (status === HTTP_STATUSES.OK) {
        return status;
      }
      return null;
    }, activateUserUUID);
  };

  return {
    userDetails,
    availabilityLogs,
    getUser,
    getUserAvailabilityLogs,
    createUser,
    updateUser,
    deactivateUser,
    activateUser,
    loading: stateManager.isBusy(),
  };
};
